import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Form, Row, Col, Input, DatePicker, Select, Card, Spin, TimePicker, InputNumber, Tag, Divider, Tooltip, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { fetchBookingAvailability, searchSpaces } from '../OfflineBookingActions';
import '../../../../assets/scss/components/OfflineBooking/BookingDetailsForm.scss';

const { Option } = Select;

const BookingDetailsForm = ({
  form,
  spaces,
  loadingSpaces,
  spaceDetails,
  loadingSpaceDetails,
  selectedSpace,
  availableChargingTypes,
  chargingType,
  availableBlocks,
  selectedChargingOption,
  onSpaceChange,
  onLoadSpaces,
  onChargingTypeChange,
  calculatedValues = {},
  triggerRecalc = () => {}
}) => {
  const dispatch = useDispatch();
  
  const { 
    availability = [], 
    loadingAvailability = false,
    spacesTotalPages = 0,
    spacesPage = 1
  } = useSelector(state => state.offlineBooking || {});
  
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [spaceSearchQuery, setSpaceSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (selectedSpace && chargingType && selectedChargingOption) {
      fetchAvailability();
    }
  }, [selectedSpace, chargingType, selectedChargingOption]);

  useEffect(() => {
    if (availability && availability.length > 0) {
      const map = {};
      availability.forEach(item => {
        const dateKey = Object.keys(item)[0];
        const dateData = item[dateKey];
        const dateStr = moment(dateKey).format('YYYY-MM-DD');
        map[dateStr] = dateData;
      });
      setAvailabilityMap(map);
      console.log('Availability map updated:', map);
    } else {
      setAvailabilityMap({});
    }
  }, [availability]);

  // Debounced search handler for spaces
  const handleSpaceSearch = useCallback((value) => {
    setSpaceSearchQuery(value);
    setIsSearching(true);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce search by 500ms
    searchTimeoutRef.current = setTimeout(() => {
      if (value && value.trim().length >= 2) {
        console.log('Searching spaces with query:', value);
        dispatch(searchSpaces(value.trim(), 1, 20, 'LK'));
      } else if (value.trim().length === 0) {
        // If search is cleared, reload initial spaces
        onLoadSpaces();
      }
      setIsSearching(false);
    }, 500);
  }, [dispatch, onLoadSpaces]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchAvailability = async () => {
    if (!selectedSpace || !chargingType || !selectedChargingOption) return;

    try {
      const startDate = moment().format('DD-MM-YYYY');
      const endDate = moment().add(3, 'months').format('DD-MM-YYYY');
      
      const eventTypeId = spaceDetails?.events?.[0]?.eventType?.id || 
                         spaceDetails?.events?.[0]?.id ||
                         selectedChargingOption.eventTypeId || 
                         selectedChargingOption.eventId;
      
      if (!eventTypeId) {
        console.warn('No eventTypeId found for availability check');
        return;
      }

      const selectedChargingType = selectedChargingOption.type || chargingType;
      const chargingTypeFormatted = selectedChargingType.charAt(0).toUpperCase() + selectedChargingType.slice(1);
      
      
      await dispatch(fetchBookingAvailability(
        selectedSpace,
        eventTypeId,
        startDate,
        endDate,
        chargingTypeFormatted
      ));
    } catch (error) {
      console.error('Error fetching availability:', error);
      message.error('Failed to load availability data');
    }
  };

  const isDateAvailable = React.useCallback((dateStr) => {
    if (!availabilityMap || Object.keys(availabilityMap).length === 0) return true;
    const data = availabilityMap[dateStr];
    if (!data) return false; 
    
    if (data.status === true) {
      if (chargingType === 'hourly' && data.slots) {
        return data.slots.length > 0;
      }
      if (data.availableSeats !== undefined) {
        return data.availableSeats > 0;
      }
      return true;
    }
    return false;
  }, [availabilityMap, chargingType]);

  const getAvailableTimeSlots = React.useCallback((dateStr) => {
    if (!availabilityMap || Object.keys(availabilityMap).length === 0) return [];
    const data = availabilityMap[dateStr];
    if (!data || !data.slots) return [];
    return data.slots;
  }, [availabilityMap]);
  
  const getAvailableSeats = React.useCallback((dateStr) => {
    if (!availabilityMap || Object.keys(availabilityMap).length === 0) return null;
    const data = availabilityMap[dateStr];
    if (!data || data.availableSeats === undefined) return null;
    return data.availableSeats;
  }, [availabilityMap]);

  const getFirstAvailableDate = React.useCallback(() => {
    if (!availabilityMap || Object.keys(availabilityMap).length === 0) {
      return moment();
    }
    
    const dates = Object.keys(availabilityMap).sort();
    
    const today = moment().startOf('day');
    for (const dateStr of dates) {
      const date = moment(dateStr);
      if (date.isSameOrAfter(today) && isDateAvailable(dateStr)) {
        return date;
      }
    }
    
    return moment();
  }, [availabilityMap, isDateAvailable]);

  const isTimeInAvailableSlots = (time, dateStr, isEndTime = false) => {
    if (chargingType !== 'hourly') return true;
    if (!time || !dateStr) return true;
    
    const slots = getAvailableTimeSlots(dateStr);
    if (!slots || slots.length === 0) {
      const dateData = availabilityMap[dateStr];
      if (dateData && dateData.status === false) return false;
      return true;
    }
    
    for (const slot of slots) {
      const startTime = moment(slot.startTime, ['h:mm A', 'HH:mm']);
      const endTime = moment(slot.endTime, ['h:mm A', 'HH:mm']);
      
      const timeToCheck = startTime.clone().hour(time.hour()).minute(time.minute());
      
      if (isEndTime) {
        if (timeToCheck.isSameOrAfter(startTime) && timeToCheck.isSameOrBefore(endTime)) {
          return true;
        }
      } else {
        if (timeToCheck.isSameOrAfter(startTime) && timeToCheck.isBefore(endTime)) {
          return true;
        }
      }
    }
    
    return false;
  };

  const disabledDate = React.useCallback((current) => {
    if (!current) return false;
    
    if (current < moment().startOf('day')) return true;
    
    if (!availabilityMap || Object.keys(availabilityMap).length === 0) return false;
    
    const dateStr = current.format('YYYY-MM-DD');
    return !isDateAvailable(dateStr);
  }, [availabilityMap, chargingType]);

  const getDisabledTime = React.useCallback((current, type = 'start') => {
    const bookingDate = form.getFieldValue('hourlyBookingDate');
    if (!bookingDate || chargingType !== 'hourly') {
      return {
        disabledHours: () => [],
        disabledMinutes: () => [],
      };
    }
    
    const dateStr = bookingDate.format('YYYY-MM-DD');
    const slots = getAvailableTimeSlots(dateStr);
    
    if (!slots || slots.length === 0) {
      return {
        disabledHours: () => [],
        disabledMinutes: () => [],
      };
    }
    
    const minimumPeriod = selectedChargingOption?.minimumBookingPeriod || 1;
    const minimumPeriodEnabled = selectedChargingOption?.minimumBookingPeriodAvailability !== false;
    const acceptHalfHour = selectedChargingOption?.acceptedHalfHourBooking || false;
    
     const disabledHours = () => {
      const disabledHoursList = [];
      
      for (let hour = 0; hour < 24; hour++) {
        let isAvailable = false;
        
        for (const slot of slots) {
          const startTime = moment(slot.startTime, ['h:mm A', 'HH:mm']);
          const endTime = moment(slot.endTime, ['h:mm A', 'HH:mm']);
          
          if (type === 'start') {
            const minutesToCheck = acceptHalfHour ? [0, 30] : [0];
            
            for (const minute of minutesToCheck) {
              const testTime = startTime.clone().hour(hour).minute(minute);
              
              if (testTime.isSameOrAfter(startTime) && testTime.isBefore(endTime)) {
                if (minimumPeriodEnabled) {
                  const timeRemaining = endTime.diff(testTime, 'hours', true);
                  if (timeRemaining >= minimumPeriod) {
                    isAvailable = true;
                    break;
                  }
                } else {
                  isAvailable = true;
                  break;
                }
              }
            }
          } else {
            const testTime00 = startTime.clone().hour(hour).minute(0);
            
            if (testTime00.isSameOrAfter(startTime) && testTime00.isSameOrBefore(endTime)) {
              isAvailable = true;
            } 
            else if (acceptHalfHour) {
              const testTime30 = startTime.clone().hour(hour).minute(30);
              if (testTime30.isSameOrAfter(startTime) && testTime30.isSameOrBefore(endTime)) {
                isAvailable = true;
              }
            }
          }
          
          if (isAvailable) break;
        }
        
        if (!isAvailable) {
          disabledHoursList.push(hour);
        }
      }
      return disabledHoursList;
    };
    
    const disabledMinutes = (selectedHour) => {
      if (!selectedHour && selectedHour !== 0) {
        return [];
      }
      
      const disabledMinutesList = [];
      
      let slotStart = null;
      let slotEnd = null;
      
      for (const slot of slots) {
        const startTime = moment(slot.startTime, ['h:mm A', 'HH:mm']);
        const endTime = moment(slot.endTime, ['h:mm A', 'HH:mm']);
        
        const testTimeStart = startTime.clone().hour(selectedHour).minute(0);
        const testTimeEnd = startTime.clone().hour(selectedHour).minute(59);
        
        if (type === 'start') {
          if (testTimeStart.isSameOrAfter(startTime) && testTimeStart.isBefore(endTime)) {
            slotStart = startTime;
            slotEnd = endTime;
            break;
          }
        } else {
          if ((testTimeStart.isSameOrAfter(startTime) && testTimeStart.isSameOrBefore(endTime)) ||
              (testTimeEnd.isAfter(startTime) && testTimeEnd.isSameOrBefore(endTime))) {
            slotStart = startTime;
            slotEnd = endTime;
            break;
          }
        }
      }
      
      if (!slotStart || !slotEnd) {
        for (let min = 0; min < 60; min++) {
          disabledMinutesList.push(min);
        }
        return disabledMinutesList;
      }
      
      const minutesToCheck = acceptHalfHour ? [0, 30] : [0];
      
      for (let min = 0; min < 60; min++) {
        if (!acceptHalfHour && min !== 0) {
          disabledMinutesList.push(min);
          continue;
        }
        
        if (acceptHalfHour && min !== 0 && min !== 30) {
          disabledMinutesList.push(min);
          continue;
        }
        
        const testTime = slotStart.clone().hour(selectedHour).minute(min);
        
        if (type === 'start') {
          if (testTime.isSameOrAfter(slotStart) && testTime.isBefore(slotEnd)) {
            if (minimumPeriodEnabled) {
              const timeRemaining = slotEnd.diff(testTime, 'hours', true);
              if (timeRemaining < minimumPeriod) {
                disabledMinutesList.push(min);
              }
            }
          } else {
            disabledMinutesList.push(min);
          }
        } else {
          const shouldDisable = testTime.isBefore(slotStart) || testTime.isAfter(slotEnd);
          
          if (shouldDisable) {
            disabledMinutesList.push(min);
          }
        }
      }
      
      return disabledMinutesList;
    };
    
    return { disabledHours, disabledMinutes };
  }, [form, chargingType, availabilityMap, selectedChargingOption, getAvailableTimeSlots]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'LKR 0.00';
    return `LKR ${Number(amount).toFixed(2)}`;
  };

  const shouldHideGuestCount = React.useCallback(() => {
    if (!spaceDetails?.events?.[0]?.eventType?.spaceCategory?.name) return false;
    const categoryName = spaceDetails.events[0].eventType.spaceCategory.name.toLowerCase();
    return categoryName.includes('shoot');
  }, [spaceDetails]);

  const isPrivateOffice = React.useCallback(() => {
    if (!spaceDetails?.events?.[0]?.eventType?.code) return false;
    const eventCode = spaceDetails.events[0].eventType.code;
    return eventCode === 'WORK_PRIV';
  }, [spaceDetails]);

  const isMeetCategory = React.useCallback(() => {
    if (!spaceDetails?.events?.[0]?.eventType?.spaceCategory?.name) return false;
    const categoryName = spaceDetails.events[0].eventType.spaceCategory.name.toLowerCase();
    return categoryName === 'meet';
  }, [spaceDetails]);

  const getSeatingArrangements = React.useCallback(() => {
    if (!isMeetCategory()) return [];
    
    const currentEvent = spaceDetails?.events?.find(event => 
      event.eventType.id === selectedChargingOption?.eventTypeId
    ) || spaceDetails?.events?.[0];
    
    return currentEvent?.seatArrangements || [];
  }, [spaceDetails, selectedChargingOption, isMeetCategory]);

  const selectedSeatingArrangement = Form.useWatch('seatingArrangement', form);

  const getSeatingArrangementCapacity = React.useCallback(() => {
    if (!isMeetCategory() || !selectedSeatingArrangement) return null;
    
    const arrangements = getSeatingArrangements();
    const selected = arrangements.find(arr => arr.seatArrangement.id === selectedSeatingArrangement);
    
    if (selected) {
      return { min: selected.min, max: selected.max };
    }
    return null;
  }, [isMeetCategory, selectedSeatingArrangement, getSeatingArrangements]);

  const getCapacity = React.useCallback(() => {
    if (isPrivateOffice()) {
      return 1;
    }

    if (isMeetCategory()) {
      const capacity = getSeatingArrangementCapacity();
      if (capacity) {
        return capacity.max;
      }
      return undefined;
    }

    if (chargingType !== 'hourly' && availabilityMap && Object.keys(availabilityMap).length > 0) {
      let startDateField = null;
      if (chargingType === 'daily') startDateField = 'dailyStartDate';
      else if (chargingType === 'weekly') startDateField = 'weeklyStartDate';
      else if (chargingType === 'monthly') startDateField = 'monthlyStartDate';
      
      if (startDateField) {
        const startDate = form.getFieldValue(startDateField);
        if (startDate) {
          const dateStr = startDate.format('YYYY-MM-DD');
          const availableSeats = getAvailableSeats(dateStr);
          if (availableSeats !== null) {
            return availableSeats;
          }
        }
      }
    }
    
    if (!spaceDetails) return undefined;
    return (
      spaceDetails?.capacity?.total ||
      spaceDetails?.capacity ||
      spaceDetails?.seatCapacity ||
      spaceDetails?.seats ||
      undefined
    );
  }, [spaceDetails, chargingType, availabilityMap, form, isPrivateOffice, isMeetCategory, getSeatingArrangementCapacity]);

  const getOperatingHours = () => {
    if (!spaceDetails) return { start: '09:00', end: '18:00' };
    
    const pricingOption = selectedChargingOption?.pricingOptions?.[0];
    if (pricingOption?.startTime && pricingOption?.endTime) {
      return { start: pricingOption.startTime, end: pricingOption.endTime };
    }
    
    const periodicalhours = spaceDetails.periodicalhours?.[0];
    if (periodicalhours?.from && periodicalhours?.to) {
      return { start: periodicalhours.from, end: periodicalhours.to };
    }
    
    if (spaceDetails.operatingHours?.start && spaceDetails.operatingHours?.end) {
      return { start: spaceDetails.operatingHours.start, end: spaceDetails.operatingHours.end };
    }
    
    return { start: '09:00', end: '18:00' };
  };

  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    return moment(timeStr, ['h:mm a', 'HH:mm', 'h:mm A', 'H:mm']);
  };

  const isTimeWithinOperatingHours = (time) => {
    if (!time || !time.isValid || !time.isValid()) return true;
    const hours = getOperatingHours();
    const startTime = parseTime(hours.start);
    const endTime = parseTime(hours.end);
    if (!startTime || !endTime) return true;
    
    const timeToCheck = startTime.clone().hour(time.hour()).minute(time.minute());
    return timeToCheck.isSameOrAfter(startTime) && timeToCheck.isSameOrBefore(endTime);
  };

  const getDefaultStartTime = () => {
    const hours = getOperatingHours();
    return parseTime(hours.start) || moment();
  };

  const getDefaultEndTime = () => {
    const startTime = getDefaultStartTime();
    return startTime ? moment(startTime).add(1, 'hour') : moment().add(1, 'hour');
  };

  // Get first available time slot for a given date
  const getFirstAvailableTimeSlot = (dateStr) => {
    if (!dateStr || chargingType !== 'hourly') return null;
    
    const slots = getAvailableTimeSlots(dateStr);
    if (!slots || slots.length === 0) {
      // If no slots, use operating hours
      return { 
        startTime: getDefaultStartTime(), 
        endTime: getDefaultEndTime() 
      };
    }
    
    // Get the first slot
    const firstSlot = slots[0];
    const startTime = moment(firstSlot.startTime, ['h:mm A', 'HH:mm']);
    
    // Calculate end time based on minimum booking period
    const minimumPeriod = selectedChargingOption?.minimumBookingPeriod || 1;
    const endTime = moment(startTime).add(minimumPeriod, 'hours');
    
    // Make sure end time doesn't exceed slot end time
    const slotEndTime = moment(firstSlot.endTime, ['h:mm A', 'HH:mm']);
    if (endTime.isAfter(slotEndTime)) {
      return { startTime, endTime: slotEndTime };
    }
    
    return { startTime, endTime };
  };
  
  const showSummary = !!(selectedSpace && spaceDetails && !loadingSpaceDetails);

  const weeklyStartDateWatch = Form.useWatch && Form.useWatch('weeklyStartDate', form);
  const numberOfWeeksWatch = Form.useWatch && Form.useWatch('numberOfWeeks', form);
  const monthlyStartDateWatch = Form.useWatch && Form.useWatch('monthlyStartDate', form);
  const numberOfMonthsWatch = Form.useWatch && Form.useWatch('numberOfMonths', form);

  useEffect(() => {
    if (chargingType === 'daily') {
      const startDate = form.getFieldValue('dailyStartDate');
      const endDate = form.getFieldValue('dailyEndDate');
      if (startDate && endDate && !form.getFieldValue('numberOfDays')) {
        const days = endDate.diff(startDate, 'days') + 1;
        form.setFieldValue('numberOfDays', days);
      }
    }
  }, [chargingType, form]);

  useEffect(() => {
    if (availabilityMap && Object.keys(availabilityMap).length > 0 && form.getFieldValue('guestCount')) {
      form.validateFields(['guestCount']).catch(() => {
      });
    }
  }, [availabilityMap, form]);

  useEffect(() => {
    if (availabilityMap && Object.keys(availabilityMap).length > 0) {
      const firstAvailableDate = getFirstAvailableDate();
      
      if (chargingType === 'hourly') {
        const currentDate = form.getFieldValue('hourlyBookingDate');
        if (!currentDate || !isDateAvailable(currentDate.format('YYYY-MM-DD'))) {
          form.setFieldValue('hourlyBookingDate', firstAvailableDate);
          
          // Set first available time slots
          const dateStr = firstAvailableDate.format('YYYY-MM-DD');
          const firstSlot = getFirstAvailableTimeSlot(dateStr);
          if (firstSlot) {
            form.setFieldValue('bookingStartTime', firstSlot.startTime);
            form.setFieldValue('bookingEndTime', firstSlot.endTime);
            
            // Calculate duration
            const startHour = firstSlot.startTime.hour();
            const startMinute = firstSlot.startTime.minute();
            const endHour = firstSlot.endTime.hour();
            const endMinute = firstSlot.endTime.minute();
            const duration = (endHour + endMinute / 60) - (startHour + startMinute / 60);
            form.setFieldValue('hourlyDuration', parseFloat(duration.toFixed(2)));
          }
        }
      } else if (chargingType === 'daily') {
        const currentStartDate = form.getFieldValue('dailyStartDate');
        if (!currentStartDate || !isDateAvailable(currentStartDate.format('YYYY-MM-DD'))) {
          form.setFieldValue('dailyStartDate', firstAvailableDate);
          form.setFieldValue('dailyEndDate', firstAvailableDate);
        }
      } else if (chargingType === 'weekly') {
        const currentDate = form.getFieldValue('weeklyStartDate');
        if (!currentDate || !isDateAvailable(currentDate.format('YYYY-MM-DD'))) {
          form.setFieldValue('weeklyStartDate', firstAvailableDate);
        }
      } else if (chargingType === 'monthly') {
        const currentDate = form.getFieldValue('monthlyStartDate');
        if (!currentDate || !isDateAvailable(currentDate.format('YYYY-MM-DD'))) {
          form.setFieldValue('monthlyStartDate', firstAvailableDate);
        }
      } else if (chargingType === 'block') {
        const currentDate = form.getFieldValue('blockBookingDate');
        if (!currentDate || !isDateAvailable(currentDate.format('YYYY-MM-DD'))) {
          form.setFieldValue('blockBookingDate', firstAvailableDate);
        }
      }
    }
  }, [availabilityMap, chargingType, form, getFirstAvailableDate, isDateAvailable]);

  useEffect(() => {
    if (spaceDetails) {
      if (shouldHideGuestCount()) {
        form.setFieldValue('guestCount', 1);
      }
      else if (isPrivateOffice()) {
        const currentGuestCount = form.getFieldValue('guestCount');
        if (!currentGuestCount || currentGuestCount !== 1) {
          form.setFieldValue('guestCount', 1);
        }
      }
      else if (isMeetCategory()) {
        const arrangements = getSeatingArrangements();
        if (arrangements.length === 1 && !form.getFieldValue('seatingArrangement')) {
          const firstArrangement = arrangements[0];
          form.setFieldValue('seatingArrangement', firstArrangement.seatArrangement.id);
          form.setFieldValue('guestCount', firstArrangement.min);
        }
      }
    }
  }, [spaceDetails, form, shouldHideGuestCount, isPrivateOffice, isMeetCategory, getSeatingArrangements]);

  return (
    <div className="booking-details-form">
      <Divider orientation="left">Booking Details</Divider>

      <Row gutter={24}>
        <Col xs={24} lg={showSummary ? 17 : 24}>
          <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            name="bookingRef"
            label="Booking Ref"
          >
            <Input placeholder="Leave empty to auto-generate" disabled/>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name="space"
            label={
              <span>
                Space
                {/* <button 
                  type="button" 
                  className="refresh-btn"
                  onClick={onLoadSpaces}
                  disabled={loadingSpaces}
                  title="Refresh spaces list"
                  style={{ marginLeft: '8px' }}
                >
                  <ReloadOutlined />
                </button> */}
              </span>
            }
            rules={[{ required: true, message: 'Please select or enter space ID' }]}
            tooltip="Type to search for spaces by name or location"
          >
            <Select 
              placeholder={loadingSpaces || isSearching ? "Loading spaces..." : "Type to search spaces..."} 
              showSearch
              loading={loadingSpaces || isSearching}
              onChange={onSpaceChange}
              onSearch={handleSpaceSearch}
              filterOption={false}
              notFoundContent={
                loadingSpaces || isSearching ? (
                  <Spin size="small" />
                ) : spaces.length === 0 ? (
                  <div style={{ padding: '12px', textAlign: 'center' }}>
                    <p>No spaces found</p>
                    {spaceSearchQuery && (
                      <p style={{ fontSize: '12px', color: '#999' }}>
                        Try a different search term
                      </p>
                    )}
                  </div>
                ) : null
              }
              allowClear
              optionLabelProp="label"
              dropdownMatchSelectWidth={false}
              virtual={true}
            >
              {spaces.map((space) => (
                <Option 
                  key={space.id} 
                  value={space.id}
                  label={space.spaceName || space.name || space.title || `Space ${space.id}`}
                >
                  <div className="space-option">
                    <div>
                      <div className="space-name">
                        {space.spaceName || space.name || space.title || `Space ${space.id}`}
                      </div>
                      <div className="space-location">
                        {space.location?.city && `${space.location.city}`}
                        {space.location?.address && ` • ${space.location.address.substring(0, 50)}${space.location.address.length > 50 ? '...' : ''}`}
                        {space.events?.[0]?.chargings?.[0]?.hourlyRate && (
                          <span className="space-rate">
                            {' • '}LKR {space.events[0].chargings[0].hourlyRate}/hr
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name="billingDate"
            label="Billing Date"
            initialValue={moment()}
            rules={[
              { required: true, message: 'Please select billing date' }
            ]}
            tooltip="Select the date when billing was issued (past 30 days allowed for backdated bookings)"
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD-MM-YYYY"
              disabledDate={(current) => {
                if (!current) return false;
                const today = moment().endOf('day');
                const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day');
                return current.isAfter(today) || current.isBefore(thirtyDaysAgo);
              }}
              onChange={(date) => {
                // Sync billing date to payment schedule date
                if (date) {
                  form.setFieldsValue({
                    paymentScheduleOn: date
                  });
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      {spaceDetails && availableChargingTypes.length > 1 && (
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="selectedChargingType"
              label="Charging Type"
              rules={[{ required: true, message: 'Please select charging type' }]}
            >
              <Select
                placeholder="Select charging type"
                onChange={onChargingTypeChange}
              >
                {availableChargingTypes.map(charging => (
                  <Option key={String(charging.id)} value={String(charging.id)}>
                    <div className="charging-option">
                      <span>{charging.name}</span>
                      <span className="charging-rate">
                        LKR {charging.hourlyRate || charging.dailyRate || 
                             charging.weeklyRate || charging.monthlyRate || 
                             charging.blockRate || 0}
                        {charging.type === 'hourly' && '/hr'}
                        {charging.type === 'daily' && '/day'}
                        {charging.type === 'weekly' && '/week'}
                        {charging.type === 'monthly' && '/month'}
                        {charging.type === 'block' && '/block'}
                      </span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <div className="charging-info">
              {availableChargingTypes.length} charging options available for this space
              {loadingAvailability && <Spin size="small" style={{ marginLeft: 8 }} />}
            </div>
          </Col>
        </Row>
      )}

      {chargingType && (
        <Row gutter={24}>
          <Col span={24}>
            <Card size="small" className="charging-type-card">
              <div className="charging-type-display">
                Charging Type: {chargingType.charAt(0).toUpperCase() + chargingType.slice(1)}
                {selectedChargingOption && (
                  <span className="charging-type-rate">
                    - LKR {selectedChargingOption.hourlyRate || selectedChargingOption.dailyRate || 
                           selectedChargingOption.weeklyRate || selectedChargingOption.monthlyRate || 
                           selectedChargingOption.blockRate || 0}
                    {chargingType === 'hourly' && '/hr'}
                    {chargingType === 'daily' && '/day'}
                    {chargingType === 'weekly' && '/week'}
                    {chargingType === 'monthly' && '/month'}
                    {chargingType === 'block' && '/block'}
                  </span>
                )}
                {loadingAvailability && (
                  <span style={{ marginLeft: 12 }}>
                    <Spin size="small" /> Loading availability...
                  </span>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {chargingType === 'hourly' && (
        <>
        <Row gutter={24} className="period-fields">
          <Col span={6}>
            <Form.Item
              name="hourlyBookingDate"
              label="Booking Date"
              rules={[{ required: true, message: 'Please select booking date' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD-MM-YYYY"
                disabledDate={(current) => {
                  if (!current) return false;
                  const today = moment().endOf('day');
                  const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day');
                  return current.isAfter(today) || current.isBefore(thirtyDaysAgo);
                }}
                onChange={(date) => {
                  if (date && availabilityMap) {
                    const dateStr = date.format('YYYY-MM-DD');
                    const slots = getAvailableTimeSlots(dateStr);
                    
                    // Set first available time slot
                    const firstSlot = getFirstAvailableTimeSlot(dateStr);
                    if (firstSlot) {
                      form.setFieldValue('bookingStartTime', firstSlot.startTime);
                      form.setFieldValue('bookingEndTime', firstSlot.endTime);
                      
                      // Calculate duration
                      const startHour = firstSlot.startTime.hour();
                      const startMinute = firstSlot.startTime.minute();
                      const endHour = firstSlot.endTime.hour();
                      const endMinute = firstSlot.endTime.minute();
                      const duration = (endHour + endMinute / 60) - (startHour + startMinute / 60);
                      form.setFieldValue('hourlyDuration', parseFloat(duration.toFixed(2)));
                      
                      // Show available slots info
                      if (slots && slots.length > 0) {
                        const slotInfo = slots.map(s => `${s.startTime} - ${s.endTime}`).join(', ');
                        message.info(`Available time slots: ${slotInfo}`, 5);
                      }
                    } else {
                      form.setFieldValue('bookingStartTime', null);
                      form.setFieldValue('bookingEndTime', null);
                    }
                  } else {
                    form.setFieldValue('bookingStartTime', null);
                    form.setFieldValue('bookingEndTime', null);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="bookingStartTime"
              label="Start Time"
              initialValue={getDefaultStartTime()}
              rules={[
                { required: true, message: 'Please select start time' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const bookingDate = form.getFieldValue('hourlyBookingDate');
                    
                    if (bookingDate) {
                      const dateStr = bookingDate.format('YYYY-MM-DD');
                      if (!isTimeInAvailableSlots(value, dateStr, false)) {
                        return Promise.reject(new Error('Start time not available. Check available slots.'));
                      }
                      
                      const slots = getAvailableTimeSlots(dateStr);
                      if (slots && slots.length > 0) {
                        const minimumPeriod = selectedChargingOption?.minimumBookingPeriod || 1;
                        const minimumPeriodEnabled = selectedChargingOption?.minimumBookingPeriodAvailability !== false;
                        
                        if (minimumPeriodEnabled) {
                          for (const slot of slots) {
                            const slotEnd = moment(slot.endTime, ['h:mm A', 'HH:mm']);
                            const slotStart = moment(slot.startTime, ['h:mm A', 'HH:mm']);
                            
                            const valueToCheck = slotStart.clone().hour(value.hour()).minute(value.minute());
                            
                            if (valueToCheck.isSameOrAfter(slotStart) && valueToCheck.isBefore(slotEnd)) {
                              const timeRemaining = slotEnd.diff(valueToCheck, 'hours', true);
                              
                              if (timeRemaining < minimumPeriod) {
                                return Promise.reject(new Error(`Start time must allow for minimum ${minimumPeriod} hour${minimumPeriod > 1 ? 's' : ''} booking`));
                              }
                              break;
                            }
                          }
                        }
                      }
                    }
                    
                    if (!isTimeWithinOperatingHours(value)) {
                      const hours = getOperatingHours();
                      return Promise.reject(new Error(`Time must be within operating hours: ${hours.start} - ${hours.end}`));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <TimePicker 
                format="HH:mm"
                style={{ width: '100%' }}
                placeholder="Select start time"
                showNow={false}
                minuteStep={selectedChargingOption?.acceptedHalfHourBooking ? 30 : 60}
                disabledTime={() => getDisabledTime(null, 'start')}
                onChange={() => {
                  const startTime = form.getFieldValue('bookingStartTime');
                  const endTime = form.getFieldValue('bookingEndTime');
                  if (startTime && endTime) {
                    const startHour = startTime.hour();
                    const startMinute = startTime.minute();
                    const endHour = endTime.hour();
                    const endMinute = endTime.minute();
                    
                    const duration = (endHour + endMinute / 60) - (startHour + startMinute / 60);
                    
                    if (duration > 0) {
                      form.setFieldValue('hourlyDuration', parseFloat(duration.toFixed(2)));
                      try { triggerRecalc(); } catch(e) {}
                    }
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="bookingEndTime"
              label="End Time"
              initialValue={getDefaultEndTime()}
              rules={[
                { required: true, message: 'Please select end time' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const bookingDate = form.getFieldValue('hourlyBookingDate');
                    const startTime = form.getFieldValue('bookingStartTime');
                    
                    if (startTime && value.isSameOrBefore(startTime)) {
                      return Promise.reject(new Error('End time must be after start time'));
                    }
                    
                    if (bookingDate) {
                      const dateStr = bookingDate.format('YYYY-MM-DD');
                      if (!isTimeInAvailableSlots(value, dateStr, true)) {
                        return Promise.reject(new Error('End time not available. Check available slots.'));
                      }
                    }
                    
                    if (!isTimeWithinOperatingHours(value)) {
                      const hours = getOperatingHours();
                      return Promise.reject(new Error(`Time must be within operating hours: ${hours.start} - ${hours.end}`));
                    }
                    
                    if (startTime) {
                      const minimumPeriod = selectedChargingOption?.minimumBookingPeriod || 1;
                      const minimumPeriodEnabled = selectedChargingOption?.minimumBookingPeriodAvailability !== false;
                      
                      if (minimumPeriodEnabled) {
                        const startHour = startTime.hour();
                        const startMinute = startTime.minute();
                        const endHour = value.hour();
                        const endMinute = value.minute();
                        
                        const duration = (endHour + endMinute / 60) - (startHour + startMinute / 60);
                        
                        if (duration < minimumPeriod) {
                          return Promise.reject(new Error(`Booking duration must be at least ${minimumPeriod} hour${minimumPeriod > 1 ? 's' : ''}`));
                        }
                      }
                    }
                    
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <TimePicker 
                format="HH:mm"
                style={{ width: '100%' }}
                placeholder="Select end time"
                showNow={false}
                minuteStep={selectedChargingOption?.acceptedHalfHourBooking ? 30 : 60}
                disabledTime={() => getDisabledTime(null, 'end')}
                onChange={() => {
                  const startTime = form.getFieldValue('bookingStartTime');
                  const endTime = form.getFieldValue('bookingEndTime');
                  if (startTime && endTime) {
                    const startHour = startTime.hour();
                    const startMinute = startTime.minute();
                    const endHour = endTime.hour();
                    const endMinute = endTime.minute();
                    
                    const duration = (endHour + endMinute / 60) - (startHour + startMinute / 60);
                    
                    if (duration > 0) {
                      form.setFieldValue('hourlyDuration', parseFloat(duration.toFixed(2)));
                      try { triggerRecalc(); } catch(e) {}
                    }
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="hourlyDuration" label="Duration (Hours)" initialValue={1}>
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Auto-calculated"
                min={1}
                precision={1}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        </>
      )}

      {/* Block Charging Fields */}
      {chargingType === 'block' && (
        <>
        <Row gutter={24} className="period-fields">
          <Col span={12}>
            <Form.Item
              name="selectedBlock"
              label="Select Block"
              rules={[{ required: true, message: 'Please select a block' }]}
            >
              <Select placeholder={`Select available block (${availableBlocks?.length || 0} blocks)`}>
                {availableBlocks.map((block, index) => (
                  <Option key={block.id || index} value={block.id || index}>
                    <div className="block-option">
                      <span>{block.name || `Block ${index + 1}`}</span>
                      <span className="block-time">
                        {block.startTime} - {block.endTime}
                        {block.price && ` • LKR ${block.price}`}
                      </span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="blockBookingDate"
              label="Block Date"
              rules={[{ required: true, message: 'Please select block date' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD-MM-YYYY"
                disabledDate={(current) => {
                  if (!current) return false;
                  const today = moment().endOf('day');
                  const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day');
                  return current.isAfter(today) || current.isBefore(thirtyDaysAgo);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        </>
      )}

      {/* Daily Charging Fields */}
      {chargingType === 'daily' && (
        <>
        <Row gutter={24} className="period-fields">
          <Col span={8}>
            <Form.Item
              name="dailyStartDate"
              label="Start Date"
              rules={[{ required: true, message: 'Please select start date' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD-MM-YYYY"
                disabledDate={(current) => {
                  if (!current) return false;
                  const today = moment().endOf('day');
                  const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day');
                  return current.isAfter(today) || current.isBefore(thirtyDaysAgo);
                }}
                onChange={() => {
                  const startDate = form.getFieldValue('dailyStartDate');
                  const endDate = form.getFieldValue('dailyEndDate');
                  if (startDate && endDate && endDate.isSameOrAfter(startDate)) {
                    const days = endDate.diff(startDate, 'days') + 1;
                    form.setFieldValue('numberOfDays', days);
                    try { triggerRecalc(); } catch(e) {}
                  }
                  if (form.getFieldValue('guestCount')) {
                    form.validateFields(['guestCount']).catch(() => {});
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="dailyEndDate"
              label="End Date"
              rules={[
                { required: true, message: 'Please select end date' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const startDate = form.getFieldValue('dailyStartDate');
                    if (startDate && value.isBefore(startDate, 'day')) {
                      return Promise.reject(new Error('End date must be same as or after start date'));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD-MM-YYYY"
                disabledDate={(current) => {
                  if (!current) return false;
                  const today = moment().endOf('day');
                  const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day');
                  return current.isAfter(today) || current.isBefore(thirtyDaysAgo);
                }}
                onChange={() => {
                  const startDate = form.getFieldValue('dailyStartDate');
                  const endDate = form.getFieldValue('dailyEndDate');
                  if (startDate && endDate && endDate.isSameOrAfter(startDate)) {
                    const days = endDate.diff(startDate, 'days') + 1;
                    form.setFieldValue('numberOfDays', days);
                    try { triggerRecalc(); } catch(e) {}
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="numberOfDays" label="Number of Days" initialValue={1}>
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Auto-calculated"
                min={1}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
        </>
      )}

      {chargingType === 'weekly' && (
        <>
        <Row gutter={24} className="period-fields">
          <Col span={12}>
            <Form.Item
              name="weeklyStartDate"
              label="Start Date"
              rules={[{ required: true, message: 'Please select start date' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD-MM-YYYY"
                disabledDate={(current) => {
                  if (!current) return false;
                  const today = moment().endOf('day');
                  const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day');
                  return current.isAfter(today) || current.isBefore(thirtyDaysAgo);
                }}
                onChange={() => {
                  // Re-validate guest count as available seats may have changed
                  if (form.getFieldValue('guestCount')) {
                    form.validateFields(['guestCount']).catch(() => {});
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="numberOfWeeks"
              label="Number of Weeks"
              initialValue={1}
              rules={[{ required: true, message: 'Please enter number of weeks' }]}
            >
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter number of weeks"
                min={1}
                max={52}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <div className="booking-period-info">
              {weeklyStartDateWatch && numberOfWeeksWatch ? (
                <span>
                  Booking Period: {moment(weeklyStartDateWatch).format('DD-MM-YYYY')} to {moment(weeklyStartDateWatch).add(numberOfWeeksWatch, 'weeks').format('DD-MM-YYYY')}
                </span>
              ) : (
                <span>Booking Period will appear here</span>
              )}
            </div>
          </Col>
        </Row>
        </>
      )}

      {/* Monthly Charging Fields */}
      {chargingType === 'monthly' && (
        <>
        <Row gutter={24} className="period-fields">
          <Col span={12}>
            <Form.Item
              name="monthlyStartDate"
              label="Start Date"
              rules={[{ required: true, message: 'Please select start date' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD-MM-YYYY"
                disabledDate={(current) => {
                  if (!current) return false;
                  const today = moment().endOf('day');
                  const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day');
                  return current.isAfter(today) || current.isBefore(thirtyDaysAgo);
                }}
                onChange={() => {
                  if (form.getFieldValue('guestCount')) {
                    form.validateFields(['guestCount']).catch(() => {});
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="numberOfMonths"
              label="Number of Months"
              initialValue={1}
              rules={[{ required: true, message: 'Please enter number of months' }]}
            >
              <InputNumber 
                style={{ width: '100%' }} 
                placeholder="Enter number of months"
                min={1}
                max={24}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <div className="booking-period-info">
              {monthlyStartDateWatch && numberOfMonthsWatch ? (
                <span>
                  Booking Period: {moment(monthlyStartDateWatch).format('DD-MM-YYYY')} to {moment(monthlyStartDateWatch).add(numberOfMonthsWatch, 'months').format('DD-MM-YYYY')}
                </span>
              ) : (
                <span>Booking Period will appear here</span>
              )}
            </div>
          </Col>
        </Row>
        </>
      )}

      {spaceDetails && isMeetCategory() && getSeatingArrangements().length > 0 && (
        <Row gutter={24} className="period-fields">
          <Col span={12}>
            <Form.Item
              name="seatingArrangement"
              label="Seating Arrangement"
              rules={[{ required: true, message: 'Please select a seating arrangement' }]}
            >
              <Select
                placeholder="Select seating arrangement"
                onChange={(value) => {
                  const arrangements = getSeatingArrangements();
                  const selected = arrangements.find(arr => arr.seatArrangement.id === value);
                  if (selected) {
                    form.setFieldValue('guestCount', selected.min);
                    form.validateFields(['guestCount']);
                  }
                }}
              >
                {getSeatingArrangements().map(arrangement => (
                  <Option 
                    key={arrangement.seatArrangement.id} 
                    value={arrangement.seatArrangement.id}
                  >
                    {arrangement.seatArrangement.name} (Min: {arrangement.min}, Max: {arrangement.max})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      )}

      {spaceDetails && !shouldHideGuestCount() && (
        <Row gutter={24} className="period-fields">
          <Col span={6}>
            <Form.Item
              name="guestCount"
              label={
                <span>
                  Guest Count
                  {(() => {
                    if (isMeetCategory()) {
                      const capacity = getSeatingArrangementCapacity();
                      if (capacity) {
                        return <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>(Min: {capacity.min}, Max: {capacity.max})</span>;
                      }
                    } else {
                      const cap = getCapacity();
                      if (cap !== undefined && cap !== null) {
                        return <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>(Max: {cap})</span>;
                      }
                    }
                    return null;
                  })()}
                  {isPrivateOffice() && (
                    <span style={{ fontSize: '12px', color: '#ff4d4f', marginLeft: '8px' }}>(Private Office - Fixed)</span>
                  )}
                  {isMeetCategory() && (
                    <span style={{ fontSize: '12px', color: '#1890ff', marginLeft: '8px' }}>(Fixed Price)</span>
                  )}
                </span>
              }
              initialValue={1}
              rules={[
                { required: true, message: 'Please enter guest count' },
                {
                  validator: (_, value) => {
                    if (value == null) return Promise.resolve();
                    
                    if (isMeetCategory()) {
                      const capacity = getSeatingArrangementCapacity();
                      if (!capacity) {
                        return Promise.reject(new Error('Please select a seating arrangement first'));
                      }
                      if (value < capacity.min) {
                        return Promise.reject(new Error(`Guest count must be at least ${capacity.min} for this seating arrangement`));
                      }
                      if (value > capacity.max) {
                        return Promise.reject(new Error(`Guest count cannot exceed ${capacity.max} for this seating arrangement`));
                      }
                      return Promise.resolve();
                    }
                    
                    if (value < 1) return Promise.reject(new Error('Guest count must be at least 1'));
                    const cap = getCapacity();
                    if (cap !== undefined && cap !== null && value > cap) {
                      return Promise.reject(new Error(`Guest count cannot exceed available capacity (${cap})`));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <InputNumber 
                style={{ width: '100%' }} 
                min={isMeetCategory() ? (getSeatingArrangementCapacity()?.min || 1) : 1}
                max={getCapacity() || 999999}
                placeholder="Enter guest count"
                disabled={isPrivateOffice() || (isMeetCategory() && !selectedSeatingArrangement)}
                onChange={() => {
                  form.validateFields(['guestCount']);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* Loading indicator */}
      {loadingSpaceDetails && (
        <div className="loading-container">
          <Spin tip="Loading space details..." />
        </div>
      )}
        </Col>

  {/* Right Side - Booking Summary Card */}
  {showSummary && (
  <Col xs={24} lg={7}>
          <Card 
            title="Booking Summary" 
            className="booking-summary-card"
            style={{ position: 'sticky', top: '20px' }}
          >
            {/* Base Rate x Duration x Guest Count */}
            <div className="summary-row">
              <span className="summary-label">
                {isMeetCategory() ? (
                  <>
                    {formatCurrency(calculatedValues.basePrice)} x {calculatedValues.duration || 0} {chargingType === 'hourly' ? 'hour' : chargingType === 'daily' ? 'day' : chargingType === 'weekly' ? 'week' : 'month'}{calculatedValues.duration > 1 ? 's' : ''}
                    {calculatedValues.guestCount > 1 && (
                      <span style={{ fontSize: '11px', color: '#999', marginLeft: '8px' }}>
                        ({calculatedValues.guestCount} guests)
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {formatCurrency(calculatedValues.basePrice)} x {calculatedValues.duration || 0} {chargingType === 'hourly' ? 'hour' : chargingType === 'daily' ? 'day' : chargingType === 'weekly' ? 'week' : 'month'}{calculatedValues.duration > 1 ? 's' : ''} x {calculatedValues.guestCount || 1} guest{(calculatedValues.guestCount || 1) > 1 ? 's' : ''}
                  </>
                )}
              </span>
            </div>
            <div className="summary-row total-row" style={{ marginTop: '8px' }}>
              <span className="summary-value" style={{ fontWeight: 'bold' }}>{formatCurrency(calculatedValues.bookingTotal)}</span>
            </div>

            {/* Savings Section */}
            {calculatedValues.totalSavings > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <div className="summary-row savings-row">
                  <span className="summary-label">
                    <Tooltip title={
                      <div style={{ padding: '4px', minWidth: '200px' }}>
                        {calculatedValues.savingsBreakdown && calculatedValues.savingsBreakdown.length > 0 ? (
                          <>
                            {calculatedValues.savingsBreakdown.map((saving, index) => (
                              <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                <span>{saving.name || 'Discount'} ({saving.percentage || 0}%)</span>
                                <span style={{ marginLeft: '12px', fontWeight: 'bold' }}>{formatCurrency(saving.value || 0)}</span>
                              </div>
                            ))}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                              <span>Savings</span>
                              <span style={{ marginLeft: '12px', fontWeight: 'bold' }}>{formatCurrency(calculatedValues.totalSavings)}</span>
                            </div>
                          </>
                        ) : (
                          <div>
                            Savings {formatCurrency(calculatedValues.totalSavings)}
                          </div>
                        )}
                      </div>
                    }>
                      Total Savings:
                    </Tooltip>
                  </span>
                  <span className="summary-value savings-value">
                    ({formatCurrency(calculatedValues.totalSavings)})
                  </span>
                </div>
              </>
            )}

            {/* Price Excluding Tax */}
            <div className="summary-row" style={{ marginTop: '12px' }}>
              <span className="summary-label">Price ex Taxes</span>
            </div>
            <div className="summary-row">
              <span className="summary-value">{formatCurrency(calculatedValues.priceAfterDiscount)}</span>
            </div>

            {/* Tax */}
            <div className="summary-row" style={{ marginTop: '12px' }}>
              <span className="summary-label">
                <Tooltip title={`Tax Amount: ${formatCurrency(calculatedValues.taxAmount)}`}>
                  Taxes information
                </Tooltip>
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-value">{formatCurrency(calculatedValues.taxAmount)}</span>
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* Final Total */}
            <div className="summary-row final-total-row">
              <span className="summary-label" style={{ fontSize: '16px', fontWeight: 'bold' }}>Total</span>
            </div>
            <div className="summary-row">
              <span className="summary-value" style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                {formatCurrency(calculatedValues.finalBookingValue)}
              </span>
            </div>
          </Card>
        </Col>
        )}
      </Row>
    </div>
  );
};

export default React.memo(BookingDetailsForm);